import { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Helper to apply random noise to a sphere geometry
function jaggedSphereGeometry(radius, widthSegments, heightSegments, noiseStrength, seed) {
  const geometry = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
  // Seeded random for repeatability
  function seededRandom(s) {
    let x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  }
  for (let i = 0; i < geometry.attributes.position.count; i++) {
    const x = geometry.attributes.position.getX(i);
    const y = geometry.attributes.position.getY(i);
    const z = geometry.attributes.position.getZ(i);
    // Use position as seed for noise
    const n = (seededRandom(x * 13.1 + y * 17.2 + z * 19.3 + (seed || 0)) - 0.5) * 2;
    const scale = 1 + n * noiseStrength;
    geometry.attributes.position.setXYZ(i, x * scale, y * scale, z * scale);
  }
  geometry.computeVertexNormals();
  return geometry;
}

// Simple particle emitter: sprays particles from origin towards camera
// No auto-despawn; particles only reset when emit is set to false
export default function CakeHitParticles({ emit, origin = [0,0,0], count = 50 }) { // Reduced from 100 to 50
  const instancedMeshRef = useRef();
  const { camera } = useThree();
  
  // Use InstancedMesh for better performance - reduces draw calls from count to 1
  const dummy = useMemo(() => new THREE.Object3D(), []);
  
  // Memoize initial velocities for all particles
  const velocities = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      // Spray direction: mostly away from camera, with some random spread (opposite direction)
      const dir = new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        (Math.random() - 0.5) * 0.5,
        1 + Math.random() * 0.2 // mostly +Z
      ).normalize();
  arr.push(dir.multiplyScalar(4 + Math.random() * 2)); // much slower
    }
    return arr;
  }, [count, emit]);

  // Memoize random scales and rotations for each particle
  const particleScales = useMemo(() => 
    Array(count).fill().map(() => 0.1 + 0.9 * Math.random()), // Scale between 0.1 and 1.0
    [count]
  );
  
  const particleRotations = useMemo(() => 
    Array(count).fill().map(() => [
      Math.random() * Math.PI * 2, // X rotation
      Math.random() * Math.PI * 2, // Y rotation  
      Math.random() * Math.PI * 2  // Z rotation
    ]),
    [count]
  );

  // Memoize random colors for each particle - dark grey, dark green, dark blue
  const particleColors = useMemo(() => {
    const colorOptions = [
      '#404040', '#303030', '#505050', // dark greys
      '#004400', '#006600', '#002200', // dark greens  
      '#000044', '#000066', '#000022'  // dark blues
    ];
    return Array(count).fill().map(() => 
      new THREE.Color(colorOptions[Math.floor(Math.random() * colorOptions.length)])
    );
  }, [count]);

  // Particle state
  const times = useRef(Array(count).fill(0));
  const startTime = useRef();

  // Only reset burst timer when emit transitions from false to true
  const prevEmit = useRef(false);
  useEffect(() => {
    if (emit && !prevEmit.current) {
      startTime.current = performance.now();
      for (let i = 0; i < count; i++) times.current[i] = 0;
    }
    prevEmit.current = emit;
  }, [emit, count]);
  
  useFrame((_, delta) => {
    if (!instancedMeshRef.current) return;
    if (!emit) {
      instancedMeshRef.current.visible = false;
      return;
    }
    if (!startTime.current) startTime.current = performance.now();
    const elapsed = (performance.now() - startTime.current) / 1000;
    
    for (let i = 0; i < count; i++) {
      times.current[i] = elapsed;
      const t = times.current[i];
      if (t > 0) {
        // Move at constant velocity forever
        const pos = new THREE.Vector3(...origin);
        pos.addScaledVector(velocities[i], t);
        
        // Update instance matrix with random scale and rotation
        dummy.position.copy(pos);
        dummy.rotation.set(...particleRotations[i]);
        const scale = particleScales[i];
        dummy.scale.set(scale, scale, scale);
        
        // Set random color for this instance
        instancedMeshRef.current.setColorAt(i, particleColors[i]);
      } else {
        // Hide by scaling to zero
        dummy.position.set(...origin);
        dummy.scale.set(0, 0, 0);
      }
      
      dummy.updateMatrix();
      instancedMeshRef.current.setMatrixAt(i, dummy.matrix);
    }
    
    instancedMeshRef.current.instanceMatrix.needsUpdate = true;
    instancedMeshRef.current.instanceColor && (instancedMeshRef.current.instanceColor.needsUpdate = true);
    instancedMeshRef.current.visible = true;
  });

  // Precompute jagged geometries for each particle - but for instanced mesh, we need single geometry
  const geometry = useMemo(() => jaggedSphereGeometry(0.08, 6, 6, 0.35, 0), []); // Simplified geometry

  return (
    <instancedMesh
      ref={instancedMeshRef}
      args={[geometry, undefined, count]}
      frustumCulled={false}
      visible={false}
    >
      <meshStandardMaterial emissive="#000000" emissiveIntensity={0.3} />
    </instancedMesh>
  );
}
